import * as React from 'react'
import { useContext } from 'react'
import Img from 'gatsby-image'
import { Flex, FlexProps } from '@src/components/Boxes'
import styled from '@emotion/styled'
import { graphql, useStaticQuery } from 'gatsby'
import { css } from '@emotion/core'
import { mediaQueries } from '@src/styles/theme'
import { ModalContext, ModalType } from '@src/components/modals/ModalController'
import Translate from '@src/components/translation/Translate'
import { AppDataContext } from './AppDataContext'
import { logEvent } from '@src/utils/logClient'

const selectorBadgeVariant = ({ responsive }: { responsive?: boolean }) => {
  if (responsive) {
    return css`
      flex-direction: row;
      width: 100%;
      font-size: 26px;

      ${mediaQueries[0]} {
        flex-direction: column;
        width: 25%;
        font-size: 18px;
        line-height: 26px;
      }
    `
  }
  return css`
    flex-direction: column;
    width: 100%;
    width: 25%;

    font-size: 18px;
    line-height: 26px;
  `
}

type SelectorBadgeProps = FlexProps & {
  responsive?: boolean
  onClick?: () => void
}
const SelectorBadge = styled(Flex)<SelectorBadgeProps>`
  margin: 5px;
  color: #2c2a50;
  background-color: #fff;
  border-radius: 24px;
  transition: box-shadow 0.2s;
  box-shadow: 0 0 0 rgba(37, 61, 229, 0);
  border: 2px solid #eaecf5;
  padding: 20px 5px;
  font-weight: 600;
  text-align: left;
  vertical-align: middle;
  touch-action: manipulation;
  word-wrap: normal;
  white-space: normal;
  cursor: pointer;
  font-size: 18px;

  &:hover {
    box-shadow: 0 8px 0 rgba(37, 61, 229, 0.05);
  }
  ${selectorBadgeVariant};
`
SelectorBadge.defaultProps = { alignItems: 'center' }

const SelectorBadgeImg = styled(Img)`
  width: 72px;
  min-width: 72px;
  margin-bottom: 5px;
  margin-left: 15px;
  margin-right: 15px;
`
SelectorBadgeImg.defaultProps = { loading: 'eager' }

export enum ConstituentButtonsType {
  LOGIN = 'login',
  SIGNUP = 'signup'
}

type ConstituentButtonsProps = {
  responsive?: boolean
  type: ConstituentButtonsType
  buttonLocation?: string
}
const ConstituentButtons: React.FC<ConstituentButtonsProps> = ({ responsive, buttonLocation, type = ConstituentButtonsType.LOGIN }) => {
  const data = useStaticQuery(graphql`
    query {
      parentBadge: file(name: { eq: "parent_badge" }) {
        childImageSharp {
          fixed(width: 72, quality: 90) {
            ...GatsbyImageSharpFixed_noBase64
          }
        }
      }
      studentBadge: file(name: { eq: "student_badge" }) {
        childImageSharp {
          fixed(width: 72, quality: 90) {
            ...GatsbyImageSharpFixed_noBase64
          }
        }
      }
      schoolLeaderBadge: file(name: { eq: "school_leader_badge" }) {
        childImageSharp {
          fixed(width: 72, quality: 90) {
            ...GatsbyImageSharpFixed_noBase64
          }
        }
      }
      teacherBadge: file(name: { eq: "teacher_badge" }) {
        childImageSharp {
          fixed(width: 72, quality: 90) {
            ...GatsbyImageSharpFixed_noBase64
          }
        }
      }
    }
  `)
  const appData = useContext(AppDataContext)
  const modalContext = useContext(ModalContext)

  function getRedirectUrl() {
    const subdomain =
      appData.data.type === 'studentmode'
        ? 'studentmode'
        : appData.data.type === 'teacher'
        ? 'teach'
        : appData.data.type === 'student'
        ? 'student'
        : 'home'
    return 'https://' + subdomain + '.classdojo.com'
  }

  function teachersButtonClick() {
    if (appData.data.type && appData.data.type === 'teacher') {
      window.location.href = getRedirectUrl()
    } else {
      if (type === ConstituentButtonsType.LOGIN) {
        logEvent({
          eventValue: window.location.href,
          eventName: 'web.teacher.external_page.from_button_link.user_signup.start.tap'
        })
        modalContext.showModal(ModalType.TeacherLogin)
      } else if (type === ConstituentButtonsType.SIGNUP) {
        if (buttonLocation === 'header') {
          logEvent({
            eventValue: window.location.href,
            eventName: 'web.teacher.external_page.from_header_link.user_signup.start.tap'
          })
        } else if (buttonLocation === 'home') {
          logEvent({
            eventValue: window.location.href,
            eventName: 'web.teacher.external_page.from_homepage.user_signup.start.tap'
          })
        }
        modalContext.showModal(ModalType.TeacherSignup)
      }
    }
  }

  function parentsButtonClick() {
    if (appData.data.type && appData.data.type === 'parent') {
      window.location.href = getRedirectUrl()
    } else {
      if (type === ConstituentButtonsType.LOGIN) {
        logEvent({
          eventValue: window.location.href,
          eventName: 'web.parent.external_page.from_button_link.user_signup.start.tap'
        })
        modalContext.showModal(ModalType.ParentLogin)
      } else if (type === ConstituentButtonsType.SIGNUP) {
        if (buttonLocation === 'header') {
          logEvent({
            eventValue: window.location.href,
            eventName: 'web.parent.external_page.from_header_link.user_signup.start.tap'
          })
        } else if (buttonLocation === 'home') {
          logEvent({
            eventValue: window.location.href,
            eventName: 'web.parent.external_page.from_homepage.user_signup.start.tap'
          })
        }
        modalContext.showModal(ModalType.ParentSignup)
      }
    }
  }

  function studentsButtonClick() {
    if (type === ConstituentButtonsType.LOGIN) {
      logEvent({
        eventValue: window.location.href,
        eventName: 'web.student.external_page.from_button_link.user_signup.start.tap'
      })
    } else if (type === ConstituentButtonsType.SIGNUP) {
      if (buttonLocation === 'header') {
        logEvent({
          eventValue: window.location.href,
          eventName: 'web.student.external_page.from_header_link.user_signup.start.tap'
        })
      } else if (buttonLocation === 'home') {
        logEvent({
          eventValue: window.location.href,
          eventName: 'web.student.external_page.from_homepage.user_signup.start.tap'
        })
      }
    }
    if (appData.data.type && appData.data.type === 'studentmode') {
      window.location.href = getRedirectUrl()
    } else {
      window.location.href = 'https://student.classdojo.com/#/qr-reader'
    }
  }

  function schoolLeaderButtonClick() {
    if (appData.data.type && appData.data.type === 'teacher') {
      window.location.href = getRedirectUrl()
    } else {
      if (type === ConstituentButtonsType.LOGIN) {
        logEvent({
          eventValue: window.location.href,
          eventName: 'web.school_leader.external_page.from_button_link.user_signup.start.tap'
        })
        modalContext.showModal(ModalType.SchoolLeaderLogin)
      } else if (type === ConstituentButtonsType.SIGNUP) {
        if (buttonLocation === 'header') {
          logEvent({
            eventValue: window.location.href,
            eventName: 'web.school_leader.external_page.from_header_link.user_signup.start.tap'
          })
        } else if (buttonLocation === 'home') {
          logEvent({
            eventValue: window.location.href,
            eventName: 'web.school_leader.external_page.from_homepage.user_signup.start.tap '
          })
        }
        modalContext.showModal(ModalType.SchoolLeaderSignup)
      }
    }
  }

  const { teacherBadge, schoolLeaderBadge, studentBadge, parentBadge } = data
  return (
    <Flex
      width="100%"
      ml="-15px"
      flexDirection={[responsive ? 'column' : 'row', 'row']}
      data-test-name={`constituent-button-container-${type}`}
    >
      <SelectorBadge responsive={responsive} onClick={teachersButtonClick} data-test-name="constituent-button-teacher">
        <SelectorBadgeImg fixed={teacherBadge.childImageSharp.fixed} />
        <Translate path="pages.home.teacher_signup" />
      </SelectorBadge>
      <SelectorBadge responsive={responsive} onClick={parentsButtonClick} data-test-name="constituent-button-parent">
        <SelectorBadgeImg fixed={parentBadge.childImageSharp.fixed} />
        <Translate path="pages.home.parent_signup" />
      </SelectorBadge>
      <SelectorBadge responsive={responsive} onClick={studentsButtonClick} data-test-name="constituent-button-student">
        <SelectorBadgeImg fixed={studentBadge.childImageSharp.fixed} />
        <Translate path="pages.home.student_signup" />
      </SelectorBadge>
      <SelectorBadge responsive={responsive} onClick={schoolLeaderButtonClick} data-test-name="constituent-button-school-leader">
        <SelectorBadgeImg fixed={schoolLeaderBadge.childImageSharp.fixed} />
        <Translate path="pages.home.administrator_signup" />
      </SelectorBadge>
    </Flex>
  )
}

export default ConstituentButtons
